import { sortBy, filter } from "lodash";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  addUseToPublicCollection,
  addUserToCollection,
  archiveCollection,
  copyCollection,
  deleteCollection,
  deleteSharedUser,
  getArchivedCollections,
  getBookmarksFromCollection,
  getCollectionOverview,
  getCollections,
  getParentChainCollection,
  getSharedUserList,
  publicCollection,
  removeAllSharedUsers,
  saveCollectionFolder,
  saveCustomBookmark,
  saveCollectionFolderWithNotes,
} from "../services/collection.service";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  setArchivedCollections,
  setCollectionIdArray,
  setCollections,
  setIsBookmarkSidebar,
  setParentCollections,
  setReadingSummary,
  setSummary,
} from "context/features/dataSlice";
import {
  getCollectionsSource,
  getParentCollections,
} from "features/Bookmarks/services/collection.service";

let archivedCollectionsRequest = null;

const useCollection = (setLoading, selectedCollectionOption = "bookmarks") => {
  const [bookmarks, setBookmarks] = useState([]);
  const [filteredBookmarks, setFilteredBookmarks] = useState([]);
  const [currentCollection, setCurrentCollection] = useState({});
  const [parentCollectionIds, setParentCollectionIds] = useState([]);
  // const [collectionIdArray, setCollectionIdArray] = useState([]);
  const [markdownNotes, setMarkdownNotes] = useState("");
  const [collectionAccessError, setCollectionAccessError] = useState(false);
  const previousId = useRef(null);

  const {
    collections,
    isSidebarOpen,
    collectionSourceId,
    collectionId,
    isBookmarkSidebar,
    collectionIdArray,
    archivedCollections,
  } = useSelector((state) => state.data);

  const params = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchUrlParams = searchParams.get("search")
    ? searchParams.get("search")
    : "";
  const [search] = useState(searchUrlParams);

  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { id } = useParams();

  const getBookmarkData = async () => {
    try {
      const data = await getCollectionsSource(collectionId, collectionSourceId);
      if (data[0].isSummaryAvailable_ysn)
        dispatch(setSummary(data[0].summary_chr));
      if (data[0].isReadingLevelAvailable_ysn)
        dispatch(setReadingSummary(data[0].articleComprehension_chr));
    } catch (e) {
      console.error(e);
    }
  };

  const getBookmarks = async (id, showLoader = true) => {
    if (!id) return;
    try {
      if (showLoader) {
        setLoading?.(true);
        setBookmarks([]);
        setFilteredBookmarks([]);
      }
      const data = await getBookmarksFromCollection(user, id);
      setBookmarks(data);
      setFilteredBookmarks(data);
    } catch (e) {
      console.error(e);
    } finally {
      if (showLoader) setLoading?.(false);
    }
  };

  const redirectAndSetHeaderParams = (data, archivedList = archivedCollections) => {
    if (!id) {
      setCurrentCollection(data[0]);
    } else {
      let item = data.find((item) => item.collectionId === Number(id));
      if (!item) {
        item = archivedList.find(
          (archived) => archived.collectionId === Number(id)
        );
      }
      if (item) {
        item = {
          ...item,
          collectionName_chr: item.name,
        };
        setCurrentCollection(item);
      }
    }
  };

  const markCollectionSubmitted = (submittedId) => {
    const updated = collections.map((collection) =>
      Number(collection.collectionId) === Number(submittedId)
        ? { ...collection, isSubmitted_ysn: true }
        : collection
    );
    dispatch(setCollections(updated));
    // currentCollection is separate local state, not derived from `collections` —
    // without this, AccessSidebar's shareLocked check stays stale right after a
    // submit and lets a share-list edit through to the backend before rejecting it.
    setCurrentCollection((prev) =>
      Number(prev?.collectionId) === Number(submittedId)
        ? { ...prev, isSubmitted_ysn: true }
        : prev
    );
  };

  const getParentCollectionIds = (data) => {
    const ids = data
      .filter((collection) => collection.parentCollectionID_lng === 0)
      .map((collection) => collection.collectionID_ids);
    setParentCollectionIds(ids);
  };

  const refreshArchivedCollections = async () => {
    archivedCollectionsRequest = null;
    const archived = await fetchArchivedCollections();
    dispatch(setArchivedCollections(archived));
    return archived;
  };

  const fetchArchivedCollectionsOnce = () => {
    if (!archivedCollectionsRequest) {
      archivedCollectionsRequest = fetchArchivedCollections().finally(() => {
        archivedCollectionsRequest = null;
      });
    }
    return archivedCollectionsRequest;
  };

  const getData = async (archivedOverride) => {
    const data = await getCollections(user);
    let newArr = [];
    sortBy(data, ["collectionId_ids"]).forEach((item) => {
      newArr.push({
        name: item.collectionName_chr,
        value: item.collectionName_chr,
        collectionId: item.collectionID_ids,
        parentCollectionId: item.parentCollectionID_lng,
        isShared_ysn: item.isShared_ysn,
        isPublic_ysn: item.isPublic_ysn,
        // GetCollections carries the teacher's feedback for submitted collections; the
        // student-facing submission endpoints do not return it.
        isSubmitted_ysn: item.isSubmitted_ysn,
        teacherNote_chr: item.teacherNote_chr,
        isTeacherNotePublished_ysn: item.isTeacherNotePublished_ysn,
      });
    });
    let archived = archivedOverride ?? archivedCollections;
    if (!archivedOverride && archived.length === 0) {
      archived = await fetchArchivedCollectionsOnce();
      dispatch(setArchivedCollections(archived));
    }
    dispatch(setCollections(newArr));
    getParentCollectionIds(data);
    redirectAndSetHeaderParams(newArr, archived);
  };

  const getCollArray = async () => {
    try {
      const data = await getParentChainCollection(id, user);
      const chain = data.map((item) => String(item));
      // console.log(data, chain);
      dispatch(setCollectionIdArray(chain));
    } catch (e) {
      console.error(e);
    }
  };

  // A teacher or admin reviewing a submission, and anyone viewing an archived
  // collection, is reading someone else's share list rather than managing their own,
  // so it has to show every person on it — including themselves when the collection
  // was shared with them. Editable views still drop the viewer, because you cannot
  // share a collection with yourself and the entry would only be noise.
  const isReadOnlyShareList =
    Boolean(params.submissionId) ||
    location.pathname.includes("/collections/archive/");

  const getUsersList = async () => {
    const data = await getSharedUserList(user, id);
    if (isReadOnlyShareList) {
      return data;
    }
    return filter(data, (singleUser) => singleUser.email !== user.email);
  };

  const shareCollectionFolder = async (payload) => {
    try {
      await addUserToCollection(payload);

      if (payload.SharedWithTypeId === 2) {
        toast.success(
          `Collection shared to distribution list "${payload.UniqueDistributionListName}" successfully.`
        );
      } else {
        const emailCount = payload.Email?.length || 0;
        if (emailCount > 1) {
          toast.success(
            `Collection shared successfully with ${emailCount} users.`
          );
        } else {
          toast.success("Collection shared successfully.");
        }
      }

      getData();
    } catch (error) {
      console.error(error.response?.status);
      if (error.response?.status === 500) {
        toast.error("This Collection is already being shared with the user");
      }
      if (error.response?.data === "Recipient Not Found") {
        toast.error("User not found!");
      }
    }
  };

  const removeSharedUser = async (id, userToRemove) => {
    try {
      await deleteSharedUser(id, user, userToRemove);
      const isDistList = userToRemove?.sharedWithTypeId === 2;
      toast.success(
        isDistList
          ? "Distribution list removed successfully."
          : "Shared User access removed successfully."
      );
      getData();
    } catch (error) {
      if (error.response?.status === 500) {
        const identifier =
          userToRemove?.email ||
          userToRemove?.generatedDistributionListName ||
          "user";
        toast.error(
          `Unable to delete ${identifier} from shared collection. Kindly Contact admin for further support.`
        );
      }
    }
  };

  const handleDeleteCollection = async (id) => {
    try {
      await deleteCollection(user, id);
      getData();
      await refreshArchivedCollections();
      toast.success("Collection deleted successfully!");
    } catch (e) {
      console.error(e);
    }
  };

  const handleRenameCollection = async (id, name) => {
    try {
      await publicCollection(user, id, name);

      await getData();
      toast.success("Collection renamed successfully!");
    } catch (e) {
      console.error(e);
    }
  };

  const handleCopyCollection = async (id) => {
    try {
      const data = await copyCollection(id);
      await getData();
      await refreshArchivedCollections();
      toast.success(`Collection copied as "${data.newCollectionName}"!`);
      return data;
    } catch (e) {
      console.error(e);
      toast.error("Failed to copy collection.");
      throw e;
    }
  };

  const handleArchiveCollection = async (id, isArchived) => {
    try {
      const data = await archiveCollection(id, isArchived);
      // Refresh the archived list first and feed it straight into getData — otherwise
      // getData falls back to the pre-archive snapshot of archivedCollections, misses
      // the collection that was just (un)archived, and currentCollection (and so the
      // Header's "•••" menu) keeps showing the stale isArchived_ysn state.
      const freshArchived = await refreshArchivedCollections();
      await getData(freshArchived);
      toast.success(
        isArchived
          ? "Collection archived successfully!"
          : "Collection unarchived successfully!"
      );
      return data;
    } catch (e) {
      console.error(e);
      toast.error(
        isArchived
          ? "Failed to archive collection."
          : "Failed to unarchive collection."
      );
      throw e;
    }
  };

  const fetchArchivedCollections = async () => {
    try {
      const data = await getArchivedCollections();
      let newArr = [];
      sortBy(data, ["collectionId_ids"]).forEach((item) => {
        newArr.push({
          name: item.collectionName_chr,
          value: item.collectionName_chr,
          collectionId: item.collectionID_ids,
          parentCollectionId: item.parentCollectionID_lng,
          isShared_ysn: item.isShared_ysn,
          isPublic_ysn: item.isPublic_ysn,
          isArchived_ysn: true,
        });
      });
      return newArr;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const saveCollectionWithNotes = async (markdownNotes, showToastr = false) => {
    try {
      const data = await saveCollectionFolderWithNotes(
        user,
        markdownNotes,
        params.id
      );
      if (showToastr) {
        toast.success("Collection Notes saved successfully!");
      }
    } catch (e) {
      console.log(e);
      toast.error("Sorry couldn't save the collection. Please try again.");
    }
  };

  const saveCollection = async (name, subNewFolder, id = null) => {
    try {
      let parent = {};
      if (subNewFolder) {
        parent = { parentCollectionId: id };
      }
      const data = await saveCollectionFolder(user, name, parent);

      toast.success("Collection created successfully.");
      await getData();
      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const fetchParentCollection = async () => {
    const data = await getParentCollections(user, 0);

    let newArr = [];
    data.forEach((item) =>
      newArr.push({
        name: item.collectionName_chr,
        value: item.collectionName_chr,
        collectionId: item.collectionID_ids,
        parentCollectionId: item.parentCollectionID_lng,
        isShared_ysn: item.isShared_ysn,
        isPublic_ysn: item.isPublic_ysn,
      })
    );
    dispatch(setParentCollections(newArr.reverse()));
  };

  const handleSaveCustomBookmark = async (bookmark, json_res) => {
    try {
      const data = await saveCustomBookmark(json_res);
      getBookmarks(id, false);
      toast.success("Saved to collection");
      dispatch(setIsBookmarkSidebar(false));
      return data;
    } catch (e) {
      toast.error("Something went wrong, please try later.");
      throw e;
    }
  };

  const handleRemoveAllSharedUsers = async () => {
    try {
      await removeAllSharedUsers(id, user);
      toast.success("Collection access to other users removed successfully!");
      getData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCollectionOverview = async () => {
    try {
      const isArchived =
        location.pathname.includes("/collections/archive/") ||
        archivedCollections.some(
          (collection) => Number(collection.collectionId) === Number(id)
        );
      const data = await getCollectionOverview(user, id, isArchived);
      setMarkdownNotes(data.note_chr);
      setCollectionAccessError(false);
      return data;
    } catch (e) {
      console.error(e);
      if (e.response?.status === 403 || e.response?.status === 404) {
        setCollectionAccessError(true);
      }
      return null;
    }
  };

  const makeCollectionPublic = async (isPublic = true) => {
    try {
      const data = await addUseToPublicCollection(user, id, isPublic);
      toast.success(
        `Collection made ${isPublic ? "public" : "private"} successfully.`
      );
      getData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleGetData = () => {
    if (location.pathname.includes("/work-cited/") && !isSidebarOpen) {
      return;
    }
    redirectAndSetHeaderParams(collections);
    if (
      !location.pathname.includes("/public") &&
      selectedCollectionOption === "bookmarks"
    ) {
      if (id && !location.pathname.includes("/teacher-submission")) getCollArray();
      if (!search && !isSidebarOpen && !isBookmarkSidebar) {
        let shouldShowLoader = bookmarks.length === 0;

        if (id !== previousId.current) {
          shouldShowLoader = true;
          previousId.current = id;
        }
        getBookmarks(id, shouldShowLoader);
      }
    }
  };

  return {
    parentCollectionIds,
    currentCollection,
    bookmarks,
    filteredBookmarks,
    setFilteredBookmarks,
    collectionIdArray,
    collectionAccessError,
    getData,
    fetchParentCollection,
    saveCollection,
    handleSaveCustomBookmark,
    getUsersList,
    shareCollectionFolder,
    removeSharedUser,
    makeCollectionPublic,
    handleDeleteCollection,
    handleRenameCollection,
    handleCopyCollection,
    handleArchiveCollection,
    fetchArchivedCollections,
    refreshArchivedCollections,
    archivedCollections,
    handleRemoveAllSharedUsers,
    handleCollectionOverview,
    setBookmarks,
    collections,
    markdownNotes,
    setMarkdownNotes,
    saveCollectionWithNotes,
    handleGetData,
    getBookmarkData,
    markCollectionSubmitted,
  };
};

export default useCollection;
